import {Coordinate, FeatureFilter} from '../../types';
import * as PropTypes from 'prop-types';
import {SymbolicDate} from '../../enums';

/**
 * Properties konfigurace mapy.
 */
export interface ConfigProps {
    /**
     * Téma konfigurace.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    topic?: string;

    /**
     * ID podkladové vrstvy.
     *
     * `null` znamená nezobrazuj žádnou podkladovou vrstvu.
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    backgroundLayer?: string | null;

    /**
     * ID popisné vrstvy.
     *
     * `null` znamená nezobrazuj žádnou popisnou vrstvu.
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    foregroundLayer?: string | null;

    /**
     * ID viditelných datových vrstev.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    layers?: string[];

    /**
     * ID rozbalených položek katalogu.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    catalogNodes?: string[];

    /**
     * Pozice středu mapy.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    location?: Coordinate;

    /**
     * Přiblížení mapy.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    zoom?: number;

    /**
     * Filtr obsahu datových vrstev.
     *
     * `undefined` znamená použij výchozí hodnotu z konfigurace mapového serveru.
     */
    filter?: FeatureFilter;
}

/**
 * Properties callbacků změny konfigurace mapy.
 */
export interface ConfigCallbackProps {
    /**
     * Callback volaný při změně parametru {@link ConfigProps.topic}.
     *
     * @param topic - Nová hodnota parametru.
     */
    onTopicChange?: (topic: string) => void;

    /**
     * Callback volaný při změně parametru {@link ConfigProps.backgroundLayer}.
     *
     * @param backgroundLayer - Nová hodnota parametru.
     */
    onBackgroundLayerChange?: (backgroundLayer: string) => void;

    /**
     * Callback volaný při změně parametru {@link ConfigProps.foregroundLayer}.
     *
     * @param foregroundLayer - Nová hodnota parametru.
     */
    onForegroundLayerChange?: (foregroundLayer: string) => void;

    /**
     * Callback volaný při změně parametru {@link ConfigProps.layers}.
     *
     * @param layers - Nová hodnota parametru.
     */
    onLayersChange?: (layers: string[]) => void;

    /**
     * Callback volaný při změně parametru {@link ConfigProps.catalogNodes}.
     *
     * @param catalogNodes - Nová hodnota parametru.
     */
    onCatalogNodesChange?: (catalogNodes: string[]) => void;

    /**
     * Callback volaný při změně parametru {@link ConfigProps.location}.
     *
     * @param location - Nová hodnota parametru.
     */
    onLocationChange?: (location: Coordinate) => void;

    /**
     * Callback volaný při změně parametru {@link ConfigProps.zoom}.
     *
     * @param zoom - Nová hodnota parametru.
     */
    onZoomChange?: (zoom: number) => void;

    /**
     * Callback volaný při změně parametru {@link ConfigProps.filter}.
     *
     * @param filter - Nová hodnota parametru.
     */
    onFilterChange?: (filter: FeatureFilter) => void;
}

/**
 * Názvy properties konfigurace mapy.
 *
 * @ignore
 */
export const configPropNames = [
    'topic',
    'backgroundLayer',
    'foregroundLayer',
    'layers',
    'catalogNodes',
    'location',
    'zoom',
    'filter',
    'topicChangeEnabled',
];

/**
 * Typy properties konfigurace mapy.
 *
 * @ignore
 */
export const configPropTypes = {
    topic: PropTypes.string,
    backgroundLayer: PropTypes.string,
    foregroundLayer: PropTypes.string,
    layers: PropTypes.arrayOf(PropTypes.string),
    catalogNodes: PropTypes.arrayOf(PropTypes.string),
    location: PropTypes.arrayOf(PropTypes.number),
    zoom: PropTypes.number,
    filter: PropTypes.shape({
        date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf(Object.keys(SymbolicDate))]),
        dateFrom: PropTypes.instanceOf(Date),
        dateTo: PropTypes.instanceOf(Date),
    }),
};

/**
 * Typy properties callbacků změny konfigurace mapy.
 *
 * @ignore
 */
export const configCallbackPropTypes = {
    onTopicChange: PropTypes.func,
    onBackgroundLayerChange: PropTypes.func,
    onForegroundLayerChange: PropTypes.func,
    onLayersChange: PropTypes.func,
    onCatalogNodesChange: PropTypes.func,
    onLocationChange: PropTypes.func,
    onZoomChange: PropTypes.func,
    onFilterChange: PropTypes.func,
};

/**
 * Vybere z properties ty, které představují konfiguraci mapy.
 *
 * @param props - Properties.
 * @returns Properties konfigurace mapy.
 * @ignore
 */
export function getConfigProps<T extends ConfigProps>(props: T): ConfigProps {
    const result: ConfigProps = {};
    for (const name of configPropNames) {
        const value = props[name];
        if (value !== undefined) {
            result[name] = value;
        }
    }
    return result;
}
